import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { BriefcaseBusiness, CalendarDays, Clock, Coffee, MapPin, User } from "lucide-react";
import Qualifications from "../SectionDetails/Qualifications";
import Avatar from "../../../../../../../components/shared/Avatar";
import { useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import { calculateSections } from "../../../../hooks/form/useCalculatedSections";
import { renderSectionsDuration } from "../../../../helpers/eventdisplay/renderSectionsDuration";
import { renderDuration } from "../../../../helpers/eventdisplay/renderDuration";
import { getPrimaryActiveResource, renderUserName } from "../../../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import { cn } from "../../../../../../../lib/utils";
import type { Event } from "../../../../types/Event";
import type { Resource } from "../../../../types/Resource";
import type { CalculatedSection, ShiftTradeItem, ShiftTradeItemProps } from "../types";

function Item({
    item,
    type,
    selectedUserShifts,
    setSelectedUserShifts,
    setError,
    view
}: ShiftTradeItemProps) {
    const { t } = useTranslation();
    const companyTimeFormat = useCompanyTimeFormat();

    const source = item as Event & Resource;

    const sections = useMemo((): CalculatedSection[] => {
        return calculateSections(source?.sections || [], source.startDate) ?? [];
    }, [source]);

    const { totalBreaksDuration, breaks } = useMemo(() => {
        const totalBreaksDuration = sections?.reduce((total: number, section) => {
            if (section.type === "break" && section.start && section.end) {
                const start = moment(section.start);
                const end = moment(section.end);

                if (start.isValid() && end.isValid()) {
                    const diff = end.diff(start, "seconds");
                    return total + (Number(diff) || 0);
                }
            }
            return total;
        }, 0);

        return {
            totalBreaksDuration,
            breaks: sections?.filter((section) => section?.type === "break")
        }
    }, [sections]);

    const onUserShiftClick = useCallback((value: ShiftTradeItem) => {
        setSelectedUserShifts((prev) => {
            const exist = prev?.find((section) => section?.id === value?.id);
            return exist
                ? prev?.filter((section) => section?.id !== value?.id)
                : prev.concat(value)
        });
        setError(null);
    }, [setSelectedUserShifts, setError]);

    const primaryJob = useMemo(() => {
        return getPrimaryActiveResource(source?.jobs || []);
    }, [source?.jobs]);

    const primaryLocation = useMemo(() => {
        return getPrimaryActiveResource(source?.locations || []);
    }, [source?.locations]);

    return (
        <div
            className={cn(
                "tw-cursor-pointer tw-border tw-rounded-lg shadow-xs tw-p-4",
                selectedUserShifts?.find((shift) => shift?.id === item?.id) && "tw-border-primary tw-shadow-sm"
            )}
            onClick={view ? undefined : () => onUserShiftClick(item)}
        >
            {type === "swap" && (
                <>
                    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
                        <CalendarDays size={19} className="tw-text-primary" />
                        <span className="tw-text-sm">
                            {moment.parseZone(sections?.[0]?.start).format("ddd, DD MMMM YYYY")}
                        </span>
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <Clock size={19} className="tw-text-primary" />
                            <span className="tw-text-sm">
                                {moment.parseZone(sections?.[0]?.start).format(companyTimeFormat)}
                                {" - "}
                                {moment.parseZone(sections?.[sections?.length - 1]?.end).format(companyTimeFormat)}
                            </span>
                        </div>
                        <div className="tw-bg-neutral-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-neutral-700 tw-font-semibold">
                            {renderSectionsDuration(
                                moment(sections?.[sections?.length - 1]?.end).diff(moment(sections?.[0]?.start), "seconds"),
                                sections
                            )}
                        </div>
                    </div>

                    <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <Coffee size={19} className="tw-text-primary" />
                            <span className="tw-text-sm tw-flex tw-items-center tw-gap-1">
                                {t("breaks")}
                                <p className="tw-text-neutral-500">{`(${breaks?.length})`}</p>
                            </span>
                        </div>
                        <div className="tw-bg-amber-100 tw-text-sm tw-px-3 tw-py-1 tw-rounded tw-text-amber-900 tw-font-semibold">
                            {totalBreaksDuration > 0 ? renderDuration(totalBreaksDuration) : "0h"}
                        </div>
                    </div>
                </>
            )}

            {(sections?.[0]?.job || primaryJob) && (
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <BriefcaseBusiness size={19} className="tw-text-primary" />
                        <span className="tw-text-sm">{t("job")}</span>
                    </div>
                    <div className="tw-bg-primary/20 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-primary tw-font-semibold">
                        {renderSettingsResourceLabel(sections?.[0]?.job || primaryJob)}
                    </div>
                </div>
            )}

            {(sections?.[0]?.location || primaryLocation) && (
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <MapPin size={19} className="tw-text-primary" />
                        <span className="tw-text-sm">{t("location")}</span>
                    </div>
                    <div className="tw-bg-purple-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-purple-950 tw-font-semibold">
                        {renderSettingsResourceLabel(sections?.[0]?.location || primaryLocation)}
                    </div>
                </div>
            )}

            <div className="tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <User size={19} className="tw-text-primary" />
                    <span className="tw-text-sm">{t("user")}</span>
                </div>
                <div className="tw-flex  tw-gap-2 tw-items-center tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-font-semibold">
                    <Avatar
                        user={(source?.user || item) as Resource}
                        size="xs"
                        textStyles="text-xs"
                    />
                    {renderUserName(source?.user || item)}
                </div>
            </div>

            {type === "swap"
                ? <Qualifications sections={sections} />
                : <Qualifications user={item} />}
        </div>
    );
}

export default Item;
